import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import styled from 'styled-components';

const FormBox = styled.div`
  border: 2px solid #e83e8c;
  border-radius: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #fce4ec, #ffffff);
  margin-bottom: 20px;

  h5 {
    color: #e83e8c;
    margin-bottom: 15px;
    font-weight: 600;
  }
`;

const HelpText = styled.div`
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 10px;
  margin-bottom: 15px;
  border-radius: 4px;
  font-size: 13px;
  color: #856404;
`;

const WordBankContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  background: #fef5e7;
  border-radius: 4px;
  border: 2px dashed #e83e8c;
  min-height: 50px;
`;

const WordTag = styled.span`
  background: linear-gradient(135deg, #e83e8c, #c2185b);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  
  button {
    background: rgba(255, 255, 255, 0.3);
    border: none;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    font-size: 14px;
    padding: 0;
    
    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }
`;

const BlanksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
`;

const BlankItem = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 8px;
  background: #f9f9f9;
  border-radius: 4px;
`;

interface FillBlanksWordBankFormProps {
  formData: any;
  onChange: (data: any) => void;
}

const FillBlanksWordBankForm: React.FC<FillBlanksWordBankFormProps> = ({ formData, onChange }) => {
  const parseContent = () => {
    try {
      const parsed = typeof formData.content === 'string' && formData.content
        ? JSON.parse(formData.content)
        : null;
      
      if (parsed) {
        return {
          text: parsed.text || '',
          answers: Array.isArray(parsed.answers) ? parsed.answers : [],
          word_bank: Array.isArray(parsed.word_bank) ? parsed.word_bank : [],
        };
      }
      
      return {
        text: '',
        answers: [],
        word_bank: [],
      };
    } catch {
      return {
        text: '',
        answers: [],
        word_bank: [],
      };
    }
  };

  const [contentData, setContentData] = useState(parseContent());
  const [newBlank, setNewBlank] = useState('');
  const [newWord, setNewWord] = useState('');

  useEffect(() => {
    onChange({
      ...formData,
      content: JSON.stringify(contentData),
    });
  }, [contentData]);

  const handleTextChange = (value: string) => {
    setContentData({
      ...contentData,
      text: value,
    });
  };

  const handleAddAnswer = () => {
    if (newBlank.trim()) {
      setContentData({
        ...contentData,
        answers: [...contentData.answers, newBlank.trim()],
      });
      setNewBlank('');
    }
  };

  const handleRemoveAnswer = (index: number) => {
    const newAnswers = contentData.answers.filter((_: any, i: number) => i !== index);
    setContentData({
      ...contentData,
      answers: newAnswers,
    });
  };

  const handleAddWord = () => {
    if (newWord.trim()) {
      setContentData({
        ...contentData,
        word_bank: [...contentData.word_bank, newWord.trim()],
      });
      setNewWord('');
    }
  };

  const handleRemoveWord = (index: number) => {
    const newWordBank = contentData.word_bank.filter((_: any, i: number) => i !== index);
    setContentData({
      ...contentData,
      word_bank: newWordBank,
    });
  };

  return (
    <FormBox>
      <h5>📝 Nội dung câu hỏi - Điền từ (chọn các từ cho sẵn)</h5>
      
      <HelpText>
        💡 <strong>Hướng dẫn:</strong> Dùng dấu gạch dưới <code>___</code> để đánh dấu chỗ trống trong đoạn văn.
        <br/>
        Ví dụ: "Lan ___ to school by bike."
      </HelpText>

      <FormGroup>
        <Label>Đoạn văn có chỗ trống: *</Label>
        <Input
          type="textarea"
          value={contentData.text || ''}
          onChange={(e) => handleTextChange(e.target.value)}
          placeholder="Nhập đoạn văn và dùng ___ để đánh dấu chỗ trống...

Ví dụ: Lan ___ to school by bike. She ___ going fast."
          rows={4}
        />
        <small className="form-text text-muted">
          Dùng <code>___</code> (3 dấu gạch dưới) để đánh dấu chỗ cần điền
        </small>
      </FormGroup>

      <FormGroup>
        <Label>Đáp án đúng cho các chỗ trống (theo thứ tự): *</Label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
          <Input
            type="text"
            value={newBlank}
            onChange={(e) => setNewBlank(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAnswer())}
            placeholder="Nhập đáp án đúng..."
            style={{ flex: 1 }}
          />
          <Button color="info" size="sm" onClick={handleAddAnswer}>
            + Thêm
          </Button>
        </div>
        
        <BlanksList>
          {(contentData.answers || []).length === 0 ? (
            <div style={{ padding: '10px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
              Chưa có đáp án nào
            </div>
          ) : (
            (contentData.answers || []).map((answer: string, index: number) => (
              <BlankItem key={index}>
                <span style={{ 
                  background: '#e83e8c', 
                  color: 'white', 
                  padding: '4px 10px', 
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  minWidth: '30px',
                  textAlign: 'center'
                }}>
                  {index + 1}
                </span>
                <span style={{ flex: 1 }}>{answer}</span>
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => handleRemoveAnswer(index)}
                  style={{ padding: '2px 8px', fontSize: '12px' }}
                >
                  ✕
                </Button>
              </BlankItem>
            ))
          )}
        </BlanksList>
      </FormGroup>

      <FormGroup>
        <Label>Ngân hàng từ (Word Bank) - từ để học sinh chọn:</Label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
          <Input
            type="text"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddWord())}
            placeholder="Nhập từ cho học sinh chọn..."
            style={{ flex: 1 }}
          />
          <Button color="secondary" size="sm" onClick={handleAddWord}>
            + Thêm từ
          </Button>
        </div>
        
        <WordBankContainer>
          {(contentData.word_bank || []).length === 0 ? (
            <span style={{ color: '#999', fontSize: '13px' }}>
              Chưa có từ nào trong ngân hàng từ
            </span>
          ) : (
            (contentData.word_bank || []).map((word: string, index: number) => (
              <WordTag key={index}>
                {word}
                <button type="button" onClick={() => handleRemoveWord(index)}>×</button>
              </WordTag>
            ))
          )}
        </WordBankContainer>
        <small className="form-text text-muted">
          💡 Thêm cả từ đúng và từ sai để học sinh phải suy nghĩ
        </small>
      </FormGroup>

      {/* Preview */}
      <div style={{ 
        background: '#f8bbd0', 
        padding: '15px', 
        borderRadius: '4px',
        marginTop: '15px'
      }}>
        <strong style={{ fontSize: '13px', color: '#880e4f' }}>📋 Xem trước:</strong>
        {contentData.text && (
          <div style={{ 
            marginTop: '10px',
            padding: '12px',
            background: 'white',
            borderRadius: '4px',
            whiteSpace: 'pre-wrap'
          }}>
            {contentData.text}
          </div>
        )}
        {(contentData.word_bank || []).length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <strong style={{ fontSize: '12px' }}>Word Bank:</strong>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '6px', 
              marginTop: '6px' 
            }}>
              {(contentData.word_bank || []).map((word: string, i: number) => (
                <span key={i} style={{
                  background: 'white',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  border: '1px solid #e83e8c',
                  fontSize: '13px'
                }}>
                  {word}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Debug JSON */}
      <details style={{ marginTop: '15px' }}>
        <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>
          🔍 Xem JSON (Debug)
        </summary>
        <pre style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '200px',
          overflow: 'auto',
          marginTop: '10px'
        }}>
          {JSON.stringify(contentData, null, 2)}
        </pre>
      </details>
    </FormBox>
  );
};

export default FillBlanksWordBankForm;
